/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 📊 Chart preference state — reusable across every chart that
/// supports the options menu. Persists per chart so each user's
/// last-picked configuration is restored next session.
///
/// Settings tracked:
///   - chartType     'curve' | 'line' | 'bar'
///   - stacked       boolean   (bar mode only)
///   - filled        boolean   (curve / line mode only)
///   - dataLabels    boolean   (overlay numeric values on the chart)
///   - topN          number    (legend series limit; rest bucketed as "Other")
///
/// Persistence: a single localStorage entry per chart id holds all
/// fields as JSON. Defaults are applied if storage is missing,
/// disabled (private mode), or fails to parse.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type ChartType = 'curve' | 'line' | 'bar'

export interface UseChartTypePreferenceOptions {
  defaultType?: ChartType
  defaultStacked?: boolean
  defaultFilled?: boolean
  defaultDataLabels?: boolean
  defaultTopN?: number
}

const STORAGE_KEY = (id: string) => `pulsar:chart:${id}`
const ALLOWED_TYPES: ChartType[] = ['curve', 'line', 'bar']

export function useChartTypePreference(id: string, opts: UseChartTypePreferenceOptions = {}) {
  const chartType   = ref<ChartType>(opts.defaultType   ?? 'curve')
  const stacked     = ref<boolean>(opts.defaultStacked   ?? true)
  const filled      = ref<boolean>(opts.defaultFilled    ?? true)
  const dataLabels  = ref<boolean>(opts.defaultDataLabels ?? false)
  const topN        = ref<number>(opts.defaultTopN       ?? 10)

  // Hydrate inside onMounted so SSR doesn't touch localStorage. The
  // hydration is one-shot — the watch below handles every subsequent
  // change.
  onMounted(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY(id))
      if (!raw) return
      const data = JSON.parse(raw) as Partial<{
        type: string
        stacked: boolean
        filled: boolean
        dataLabels: boolean
        topN: number
      }>
      if (data.type && (ALLOWED_TYPES as string[]).includes(data.type)) {
        chartType.value = data.type as ChartType
      }
      if (typeof data.stacked === 'boolean') stacked.value = data.stacked
      if (typeof data.filled === 'boolean') filled.value = data.filled
      if (typeof data.dataLabels === 'boolean') dataLabels.value = data.dataLabels
      if (typeof data.topN === 'number' && Number.isFinite(data.topN)) {
        // Clamp to a sane window so an out-of-band stored value
        // doesn't render an unusable chart.
        topN.value = Math.max(1, Math.min(50, Math.round(data.topN)))
      }
    }
    catch {
      // Storage disabled / quota / corrupted JSON — fall back to defaults.
    }
  })

  // Persist on any change. We bundle into one JSON write rather than
  // a key-per-field so reads stay cheap and atomic.
  watch([chartType, stacked, filled, dataLabels, topN], () => {
    try {
      localStorage.setItem(STORAGE_KEY(id), JSON.stringify({
        type: chartType.value,
        stacked: stacked.value,
        filled: filled.value,
        dataLabels: dataLabels.value,
        topN: topN.value,
      }))
    }
    catch { /* noop */ }
  })

  return { chartType, stacked, filled, dataLabels, topN }
}
